/**
 * Your updates, drawn to the reference.
 *
 * "Your updates" with a settings button; All / Invites / Messages / Friends
 * with counts; Today as a stack of cards, each with the unread dot, the
 * person (or the event) with a small badge of what kind of update it is, the
 * words with names in bold, when it happened and the one button that answers
 * it; older days folded under their own heading. On a wide window the
 * notification preferences sit beside it, with Stay in the loop and the pug.
 *
 * Backed by real notification records, so the screen remembers what you have
 * seen, shows things that leave no row behind, and keeps showing them after
 * the thing they describe is gone. Friend requests and party invites answer
 * from the card itself.
 */

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, endpoints } from '@/lib/api';
import { go } from '@/lib/hash';
import { cn } from '@/lib/cn';
import { Icon } from '@/icons/Icon';
import type { Friendship, PublicUser } from '@/lib/types';
import { useInvites } from '@/components/Invites';
import { Avatar } from '@/components/Avatar';
import { Button, useToast } from '@/components/ui';
import { Tooltip } from '@/components/Tooltip';
import { useMinWidth } from '@/lib/viewport';
import {
  clearNotifications, markRead, useNotifications, type Notification
} from '@/lib/notifications';

type Tab = 'all' | 'invites' | 'messages' | 'friends';

const PANEL = 'pugo-panel rounded-[calc(24px*var(--radius-mul,1))] bg-surface-1 shadow-[inset_0_0_0_1px_var(--line-soft)]';

/** Which tab a notification belongs to, beyond All. */
function tabOf(n: Notification): Tab | null {
  if (n.kind === 'partyInvite' || n.kind === 'crewInvite' || n.kind === 'crewEvent' || n.group === 'party') return 'invites';
  if (n.group === 'chat') return 'messages';
  if (n.group === 'social') return 'friends';
  return null;
}

/** The small badge over the picture, in the tone of the kind. */
function badgeOf(n: Notification): { icon: string; bg: string } {
  if (n.kind === 'friendRequest' || n.kind === 'friendAccepted') return { icon: 'addFriend', bg: 'var(--good)' };
  if (n.kind === 'crewEvent') return { icon: 'calendar', bg: 'var(--accent)' };
  if (n.group === 'chat') return { icon: 'chat', bg: 'var(--accent)' };
  if (n.group === 'party') return { icon: n.kind === 'partyInvite' ? 'dice' : 'squads', bg: 'var(--accent)' };
  if (n.group === 'crew') return { icon: n.icon || 'shield', bg: 'var(--accent)' };
  if (n.group === 'you') return { icon: n.icon || 'star', bg: 'var(--good)' };
  return { icon: n.icon || 'info', bg: 'var(--text-mute)' };
}

/** "12 min ago", "1 hour ago". */
function whenText(at: number) {
  const s = Math.max(0, (Date.now() - at) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return Math.floor(s / 60) + ' min ago';
  const h = Math.floor(s / 3600);
  if (s < 86400) return h + (h === 1 ? ' hour ago' : ' hours ago');
  const d = Math.floor(s / 86400);
  if (d < 7) return d + (d === 1 ? ' day ago' : ' days ago');
  return new Date(at).toLocaleDateString([], { day: 'numeric', month: 'short' });
}

function dayKey(at: number): 'today' | 'yesterday' | 'earlier' {
  const d = new Date(at).toDateString();
  if (d === new Date().toDateString()) return 'today';
  if (d === new Date(Date.now() - 86400000).toDateString()) return 'yesterday';
  return 'earlier';
}

export function NotificationsView() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [tab, setTab] = useState<Tab>('all');
  const beside = useMinWidth(1280);

  const q = useNotifications();
  const all = useMemo(() => q.data?.notifications ?? [], [q.data]);
  const unread = q.data?.unread ?? 0;

  const invites = useInvites();
  const friends = useQuery({ queryKey: ['friends'], queryFn: endpoints.friends });
  const incoming = friends.data?.incoming ?? [];

  const act = useMutation({
    mutationFn: ({ path }: { path: string; note: string }) => api.post(path, {}),
    onSuccess: (_d, v) => {
      void queryClient.invalidateQueries({ queryKey: ['friends'] });
      void queryClient.invalidateQueries({ queryKey: ['invites'] });
      void queryClient.invalidateQueries({ queryKey: ['parties'] });
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
      toast(v.note, 'good');
    },
    onError: (err: Error) => toast(err.message, 'bad')
  });

  /* Seen is read: a moment after the list is on screen, everything in it
     counts as read - except invites and friend requests, which stay marked
     until you answer them (or join the party). */
  useEffect(() => {
    const ids = all.filter((n) => !n.read && n.kind !== 'partyInvite' && n.kind !== 'friendRequest').map((n) => n.id);
    if (!ids.length) return;
    const t = window.setTimeout(() => void markRead(queryClient, ids), 1500);
    return () => window.clearTimeout(t);
  }, [all, queryClient]);

  const count = (t: Tab) => all.filter((n) => !n.read && (t === 'all' || tabOf(n) === t)).length;
  const tabs: [Tab, string][] = [['all', 'All'], ['invites', 'Invites'], ['messages', 'Messages'], ['friends', 'Friends']];
  const shown = tab === 'all' ? all : all.filter((n) => tabOf(n) === tab);

  const groups = {
    today: shown.filter((n) => dayKey(n.createdAt) === 'today'),
    yesterday: shown.filter((n) => dayKey(n.createdAt) === 'yesterday'),
    earlier: shown.filter((n) => dayKey(n.createdAt) === 'earlier')
  };

  function open(n: Notification) {
    void markRead(queryClient, [n.id]);
    if (n.route && n.route !== 'notifications') go(n.route);
  }

  const card = (n: Notification, i: number) => {
    const invite = n.kind === 'partyInvite' ? invites.find((x) => x.party?.id === n.meta?.partyId) : undefined;
    const request = n.kind === 'friendRequest' ? incoming.find((f: Friendship) => f.user.id === n.actor?.id) : undefined;
    return (
      <UpdateCard
        key={n.id}
        n={n}
        index={i}
        invite={invite}
        request={request}
        busy={act.isPending}
        onOpen={() => open(n)}
        onDismiss={() => void clearNotifications(queryClient, n.id)}
        onAct={(path, note) => { void markRead(queryClient, [n.id]); act.mutate({ path, note }); }}
      />
    );
  };

  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <div className="mx-auto w-full max-w-[1440px] px-4 py-5 sm:px-6 xl:px-8">
        <div className="flex items-start gap-5">
          {/* ---- the feed ---- */}
          <section aria-labelledby="updates-title" className={cn(PANEL, '@container min-w-0 flex-1 p-5 sm:p-8 animate-[fade-in_280ms_ease-out] motion-reduce:animate-none')}>
            <header className="flex items-start gap-4">
              <div className="min-w-0 flex-1">
                <h1 id="updates-title" className="m-0 text-[30px] leading-tight font-extrabold tracking-[-.03em] text-ink sm:text-[38px]">Your updates</h1>
                <p className="mt-1 mb-0 text-[15.5px] text-mute">Notifications about your games, friends and activity.</p>
              </div>
              <Tooltip content="Notification settings">
                <button
                  type="button"
                  aria-label="Notification settings"
                  onClick={() => go('settings/notifications')}
                  className="grid h-11 w-11 flex-none place-items-center rounded-[calc(12px*var(--radius-mul,1))] text-mute outline-none transition-[background-color,color,transform] duration-[250ms] hover:rotate-45 hover:bg-surface-2 hover:text-ink focus-visible:shadow-[0_0_0_2px_var(--accent)] motion-reduce:hover:rotate-0"
                >
                  <Icon name="settings" size={22} />
                </button>
              </Tooltip>
            </header>

            <div role="tablist" aria-label="Filter updates" className="no-scrollbar -mx-1 mt-[18px] flex items-center gap-2.5 overflow-x-auto px-1 pt-2 pb-3">
              {tabs.map(([id, label]) => {
                const on = tab === id;
                const n = count(id);
                return (
                  <button
                    key={id}
                    type="button"
                    role="tab"
                    aria-selected={on}
                    onClick={() => setTab(id)}
                    className={cn(
                      'flex h-12 flex-none items-center gap-3 rounded-[calc(14px*var(--radius-mul,1))] px-6 text-[15.5px] font-semibold outline-none',
                      'transition-[background-color,color,box-shadow] duration-[250ms] ease-[var(--ease-out)] focus-visible:shadow-[0_0_0_2px_var(--accent)]',
                      on ? 'bg-accent text-accent-fg shadow-[0_8px_20px_-10px_color-mix(in_srgb,var(--accent)_80%,transparent)]' : 'bg-surface-1 text-ink shadow-[inset_0_0_0_1px_var(--line)] hover:bg-surface-2'
                    )}
                  >
                    {label}
                    <span className={cn('grid h-6 min-w-6 place-items-center rounded-full px-1.5 text-[12.5px] font-bold tabular-nums', on ? 'bg-accent-fg text-accent' : 'bg-accent-soft text-accent')}>
                      {n}
                    </span>
                  </button>
                );
              })}
            </div>

            <div key={tab} className="animate-[fade-in_250ms_ease-out] motion-reduce:animate-none">
              {q.isLoading ? (
                <div className="mt-6 flex flex-col gap-3" aria-busy="true" aria-label="Loading updates">
                  {[0, 1, 2].map((i) => <div key={i} className="h-[112px] animate-pulse rounded-[calc(18px*var(--radius-mul,1))] bg-surface-2" />)}
                </div>
              ) : !shown.length ? (
                <div className="mt-10 mb-6 flex flex-col items-center text-center">
                  <span className="grid h-16 w-16 place-items-center rounded-full bg-accent-soft text-accent"><Icon name="bell" size={28} /></span>
                  <p className="mt-4 mb-0 text-[18px] font-bold text-ink">{tab === 'all' ? 'You are all caught up' : 'Nothing here yet'}</p>
                  <p className="mt-1 mb-0 text-[14.5px] text-mute">Invites, messages, friends and crew news all land here.</p>
                </div>
              ) : (
                <>
                  <div className="mt-7 mb-3 flex items-baseline gap-3">
                    <h2 className="m-0 text-[22px] font-bold text-ink">Today</h2>
                    <span className="flex-1" />
                    <span className="text-[14.5px] text-mute">{groups.today.filter((n) => !n.read).length} new {groups.today.filter((n) => !n.read).length === 1 ? 'update' : 'updates'}</span>
                    {unread ? (
                      <button type="button" onClick={() => void markRead(queryClient)} className="rounded-[8px] px-1.5 text-[13.5px] font-semibold text-accent outline-none hover:underline focus-visible:shadow-[0_0_0_2px_var(--accent)]">
                        Mark all read
                      </button>
                    ) : null}
                  </div>
                  {groups.today.length ? (
                    <ul className="m-0 flex list-none flex-col gap-3 p-0">{groups.today.map(card)}</ul>
                  ) : (
                    <p className="m-0 rounded-[calc(18px*var(--radius-mul,1))] bg-surface-2 px-5 py-4 text-[14.5px] text-mute">Nothing new today.</p>
                  )}
                  {groups.yesterday.length ? <Folded label="Yesterday" list={groups.yesterday} render={card} /> : null}
                  {groups.earlier.length ? <Folded label="Earlier" list={groups.earlier} render={card} /> : null}
                  <div className="mt-5 flex justify-end">
                    <button type="button" onClick={() => void clearNotifications(queryClient)} className="rounded-[8px] px-2 py-1 text-[13px] font-semibold text-mute outline-none transition-colors hover:text-bad focus-visible:shadow-[0_0_0_2px_var(--accent)]">
                      Clear all
                    </button>
                  </div>
                </>
              )}
            </div>
          </section>

          {/* ---- preferences, on a wide window ---- */}
          {beside ? <Preferences /> : null}
        </div>

        {!beside ? <div className="mt-5"><Preferences /></div> : null}
      </div>
    </div>
  );
}

/** Yesterday and earlier, folded until you open them. */
function Folded({ label, list, render }: { label: string; list: Notification[]; render: (n: Notification, i: number) => React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-5">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="pugo-panel flex w-full items-center gap-3 rounded-[calc(18px*var(--radius-mul,1))] bg-surface-1 px-6 py-5 text-left shadow-[inset_0_0_0_1px_var(--line-soft)] outline-none transition-[background-color] duration-[250ms] hover:bg-surface-2 focus-visible:shadow-[0_0_0_2px_var(--accent)]"
      >
        <span className="flex-1 text-[20px] font-bold text-ink">{label}</span>
        <span className="text-[14.5px] text-mute">{list.length} {list.length === 1 ? 'update' : 'updates'}</span>
        <Icon name="chevronD" size={18} className={cn('text-mute transition-transform duration-[280ms] ease-[var(--ease-out)]', open && 'rotate-180')} />
      </button>
      <div className={cn('grid transition-[grid-template-rows,opacity] duration-[320ms] ease-[var(--ease-out)]', open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0')}>
        <ul className="m-0 flex list-none flex-col gap-3 overflow-hidden p-0 pt-3" inert={!open ? true : undefined}>
          {list.map(render)}
        </ul>
      </div>
    </div>
  );
}

/** One update, as a card with the button that answers it. */
function UpdateCard({ n, index, invite, request, busy, onOpen, onDismiss, onAct }: {
  n: Notification;
  index: number;
  invite: ReturnType<typeof useInvites>[number] | undefined;
  request: Friendship | undefined;
  busy: boolean;
  onOpen: () => void;
  onDismiss: () => void;
  onAct: (path: string, note: string) => void;
}) {
  const badge = badgeOf(n);
  const count = typeof n.meta?.count === 'number' ? (n.meta.count as number) : 0;

  /* The name in bold, where the title starts with it. */
  const name = n.actor?.displayName;
  const title = name && n.title.startsWith(name)
    ? <><b className="font-bold">{name}</b>{n.title.slice(name.length)}</>
    : <b className="font-bold">{n.title}</b>;

  /* A detail line under the words, when there is one worth showing. */
  let detail: React.ReactNode = null;
  if (invite?.party) {
    detail = <><Icon name="friends" size={16} />{invite.party.memberCount ?? invite.party.members?.length ?? 0}/{invite.party.size} players</>;
  } else if (n.kind === 'crewEvent') {
    detail = <><Icon name="calendar" size={16} />{n.meta?.started ? 'On now' : 'Planned by your crew'}</>;
  } else if (count > 1) {
    detail = <><Icon name="chat" size={16} />{count} messages</>;
  }

  /* The one button that answers it. */
  const route = n.route && n.route !== 'notifications' ? n.route : null;
  let action: React.ReactNode;
  const stop = (fn: () => void) => (e: React.MouseEvent) => { e.stopPropagation(); fn(); };
  if (request) {
    action = (
      <span className="flex gap-2">
        <Button kind="primary" disabled={busy} onClick={stop(() => onAct('/api/friends/' + request.id + '/accept', 'You are now friends with ' + request.user.displayName))}>Accept</Button>
        <Button kind="outline" disabled={busy} onClick={stop(() => onAct('/api/friends/' + request.id + '/decline', 'Request declined'))}>Decline</Button>
      </span>
    );
  } else if (invite) {
    action = (
      <span className="flex gap-2">
        <Button kind="primary" disabled={busy} onClick={stop(() => { onAct('/api/invites/' + invite.id + '/accept', 'Joined the party'); if (invite.party) go('party/' + invite.party.id); })}>Join party</Button>
        <Button kind="outline" disabled={busy} onClick={stop(() => onAct('/api/invites/' + invite.id + '/decline', 'Invite declined'))}>Decline</Button>
      </span>
    );
  } else {
    const label =
      n.kind === 'friendAccepted' ? 'View profile'
        : n.kind === 'crewEvent' ? 'View event'
          : n.group === 'chat' ? 'Reply'
            : n.group === 'party' ? (n.kind === 'partyJoined' ? 'Return to party' : 'View party')
              : n.group === 'crew' ? 'View crew'
                : 'Open';
    const primary = n.group === 'party' || n.group === 'chat';
    action = route || (n.kind === 'friendAccepted' && n.actor) ? (
      <Button
        kind={primary ? 'primary' : 'outline'}
        className={primary ? undefined : 'shadow-[inset_0_0_0_1.5px_var(--accent)] text-accent'}
        onClick={stop(() => { onOpen(); if (n.kind === 'friendAccepted' && n.actor) go('u/' + n.actor.username); })}
      >
        {label}
      </Button>
    ) : null;
  }

  return (
    <li
      style={{ animationDelay: Math.min(index, 8) * 35 + 'ms' }}
      className="animate-[fade-in_300ms_ease-out_backwards] motion-reduce:animate-none"
    >
      <div
        role="button"
        tabIndex={0}
        /* For the stylesheet: a scene draws unread in its own way. */
        data-note={n.read ? 'read' : 'unread'}
        onClick={onOpen}
        onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) { e.preventDefault(); onOpen(); } }}
        className={cn(
          'group relative flex cursor-pointer flex-wrap items-center gap-x-4 gap-y-3 rounded-[calc(18px*var(--radius-mul,1))] bg-surface-1 py-4 pr-4 pl-3 outline-none @xl:flex-nowrap @xl:pr-5',
          'shadow-[inset_0_0_0_1px_var(--line-soft)] transition-[background-color,box-shadow,transform] duration-[250ms] ease-[var(--ease-out)]',
          'hover:bg-surface-2 hover:shadow-[inset_0_0_0_1px_var(--line),var(--shadow-1)] focus-visible:shadow-[0_0_0_2px_var(--accent)]'
        )}
      >
        {/* unread dot */}
        <span aria-label={n.read ? undefined : 'Unread'} role={n.read ? undefined : 'img'} className={cn('h-2.5 w-2.5 flex-none rounded-full transition-opacity duration-[250ms]', n.read ? 'opacity-0' : 'bg-accent')} />

        {/* the person, or the kind */}
        <span className="relative flex-none">
          {n.actor ? (
            <Avatar user={n.actor as PublicUser} size={76} />
          ) : (
            <span className="smooth-clip grid h-[76px] w-[76px] place-items-center rounded-[calc(18px*var(--radius-mul,1))] bg-accent-soft text-accent">
              {n.kind === 'crewEvent' || n.group === 'system' ? <Icon name={n.icon || 'bell'} size={30} /> : <img src="./logo.png" alt="" className="h-14 w-14 object-contain" />}
            </span>
          )}
          <span
            aria-hidden
            className="absolute -right-1 -bottom-1 grid h-7 w-7 place-items-center rounded-full text-white ring-[3px] ring-[var(--bg-1)]"
            style={{ background: badge.bg }}
          >
            <Icon name={badge.icon} size={14} />
          </span>
        </span>

        {/* the words */}
        <span className="min-w-0 flex-1 basis-[200px]">
          <span className={cn('block text-[16.5px] leading-snug text-ink', n.read && 'text-dim')}>{title}</span>
          {n.body ? <span className="mt-0.5 block truncate text-[14.5px] text-mute">{n.body}</span> : null}
          {detail ? <span className="mt-1.5 flex items-center gap-2 text-[13.5px] text-mute">{detail}</span> : null}
        </span>

        <span className="flex flex-none items-center gap-4 @xl:ml-2">
          <span className="text-[13.5px] whitespace-nowrap text-mute tabular-nums">{whenText(n.createdAt)}</span>
          {action}
          <Tooltip content="Dismiss">
            <button
              type="button"
              aria-label="Dismiss"
              onClick={(e) => { e.stopPropagation(); onDismiss(); }}
              className="grid h-8 w-8 place-items-center rounded-[10px] text-ghost opacity-0 outline-none transition-[opacity,color,background-color] duration-200 group-hover:opacity-100 hover:bg-surface-3 hover:text-bad focus-visible:opacity-100 focus-visible:shadow-[0_0_0_2px_var(--accent)]"
            >
              <Icon name="close" size={14} />
            </button>
          </Tooltip>
        </span>
      </div>
    </li>
  );
}

/** Notification preferences, each a way into its part of Settings. */
function Preferences() {
  const items: [string, string, string][] = [
    ['dice', 'Game invites', 'Party invites, events and game activity'],
    ['friends', 'Friend activity', 'Friend requests, online status, achievements'],
    ['chat', 'Messages', 'Direct messages and mentions'],
    ['megaphone', 'Product updates', 'New features, events and more']
  ];
  return (
    <aside aria-labelledby="prefs-title" className={cn(PANEL, 'flex w-full flex-col p-6 xl:w-[420px] xl:flex-none animate-[fade-in_340ms_ease-out] motion-reduce:animate-none')}>
      <h2 id="prefs-title" className="m-0 text-[24px] font-bold tracking-[-.02em] text-ink">Notification preferences</h2>
      <p className="mt-1 mb-0 text-[15px] text-mute">Choose what you&apos;d like to be notified about.</p>
      <ul className="m-0 mt-4 flex list-none flex-col border-y border-line-soft p-0 py-2">
        {items.map(([icon, title, note]) => (
          <li key={title}>
            <button
              type="button"
              onClick={() => go('settings/notifications')}
              className="group flex w-full items-center gap-4 rounded-[calc(14px*var(--radius-mul,1))] px-2 py-3.5 text-left outline-none transition-[background-color] duration-[250ms] hover:bg-surface-2 focus-visible:shadow-[0_0_0_2px_var(--accent)]"
            >
              <Icon name={icon} size={24} className="flex-none text-ink" />
              <span className="min-w-0 flex-1">
                <span className="block text-[15.5px] font-semibold text-ink">{title}</span>
                <span className="block truncate text-[13.5px] text-mute">{note}</span>
              </span>
              <Icon name="chevronR" size={18} className="flex-none text-mute transition-transform duration-[250ms] group-hover:translate-x-0.5" />
            </button>
          </li>
        ))}
      </ul>
      <div className="mt-6 flex gap-3 rounded-[calc(18px*var(--radius-mul,1))] bg-accent-soft p-5">
        <Icon name="bolt" size={22} className="mt-0.5 flex-none text-ink" />
        <div>
          <p className="m-0 text-[16px] font-bold text-ink">Stay in the loop</p>
          <p className="mt-1 mb-0 text-[14.5px] leading-relaxed text-dim">Get the updates that matter, so you never miss a game with your friends.</p>
          <button type="button" onClick={() => go('settings/notifications')} className="group mt-2 flex items-center gap-1.5 rounded-[6px] text-[14.5px] font-semibold text-accent outline-none hover:underline focus-visible:shadow-[0_0_0_2px_var(--accent)]">
            Manage preferences
            <Icon name="arrowR" size={15} className="transition-transform duration-[250ms] group-hover:translate-x-0.5" />
          </button>
        </div>
      </div>
    </aside>
  );
}
